import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreHoriz,
  Link as LinkIcon,
  Storage as PostgresIcon,
  Inbox as SqsIcon,
  ViewList as DdbIcon,
  Folder as S3Icon,
  Psychology as LlmIcon,
  VpnKey as SecretIcon,
  Extension as GenericIcon,
  OpenInNew,
  DeleteOutline as UnbindIcon,
} from "@mui/icons-material";

import {
  getKnServiceBindings,
  unbindKnService,
} from "../../helpers/utils";
import { useBindingGraph } from "../../HOC/BindingGraphProvider";
import BindDialog from "./BindDialog";
import UnbindConfirmDialog from "./UnbindConfirmDialog";

const KIND_META = {
  postgres: { label: "Postgres", Icon: PostgresIcon, tint: "#38bdf8" },
  awssqs: { label: "SQS", Icon: SqsIcon, tint: "#f472b6" },
  awssimplequeue: { label: "SQS", Icon: SqsIcon, tint: "#f472b6" },
  awsdynamodb: { label: "DynamoDB", Icon: DdbIcon, tint: "#a78bfa" },
  awss3bucket: { label: "S3 Bucket", Icon: S3Icon, tint: "#fbbf24" },
  "llm-gateway": { label: "LLM Gateway", Icon: LlmIcon, tint: "#34d399" },
  llm_gateway: { label: "LLM Gateway", Icon: LlmIcon, tint: "#34d399" },
  DaariSecret: { label: "Secret", Icon: SecretIcon, tint: "#94a3b8" },
};

const kindMeta = (k) =>
  KIND_META[k] || { label: k || "Service", Icon: GenericIcon, tint: "#94a3b8" };

// Map a service kind label back to the /daari catalog path used by the
// deep-link (?openInstance=<path>/<k8sName>). Matches the mapping in
// UnifiedAppCard's chip click.
const kindToPath = (kind) => {
  if (kind === "llm_gateway") return "llm-gateway";
  return kind;
};

/**
 * Bindings tab inside AppFlowModal. Read-only until the unbind primitive
 * lands (coord PR-7); until then the Unbind menu item is disabled with
 * a tooltip. The Bind button opens a two-step dialog (BindDialog).
 *
 * Data source: GET /api/kn-service/{name}/bindings (coord PR #228).
 */
export default function BindingsPanel({
  appName,
  showError,
  showSuccess,
  onNavigateToDaari,
  currentUser,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [menuState, setMenuState] = useState({ anchor: null, entry: null });
  const [unbindTarget, setUnbindTarget] = useState(null);
  const [unbindSubmitting, setUnbindSubmitting] = useState(false);

  const { invalidateAfterMutation: invalidateBindingGraph } = useBindingGraph();

  const refresh = useCallback(() => {
    if (!appName) return;
    setLoading(true);
    // Refresh THIS app's bindings for the modal (needs the latest kind +
    // k8sName resolution) and simultaneously invalidate the project-wide
    // graph so the underlying dashboards re-render with fresh chip strips.
    getKnServiceBindings({ name: appName }, (result, err) => {
      setLoading(false);
      if (err) {
        showError?.(err);
        return;
      }
      setBindings(Array.isArray(result) ? result : []);
    });
    invalidateBindingGraph();
  }, [appName, showError, invalidateBindingGraph]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const closeMenu = () => setMenuState({ anchor: null, entry: null });

  const handleBindSuccess = useCallback(
    (bindingName) => {
      setBindOpen(false);
      showSuccess?.(
        bindingName
          ? `Binding created — app restarting to pick up ${bindingName}…`
          : "Binding created — app restarting…"
      );
      // Give the coordinator a moment to write the secret + patch envFrom
      // before we re-fetch (kpack/knative reconcile is not instant).
      setTimeout(refresh, 1200);
    },
    [refresh, showSuccess]
  );

  const handleUnbind = useCallback(
    (entry, restart) => {
      if (!entry) return;
      // Prefer the k8s secret name (unambiguous) when available; fall
      // back to the display name (also accepted by the coord endpoint).
      const bindingKey = entry.k8sName || entry.displayName;
      if (!bindingKey) {
        showError?.("Can't unbind — binding has no k8sName or displayName");
        return;
      }
      setUnbindSubmitting(true);
      unbindKnService(
        { appName, bindingKey, restart },
        (result, err) => {
          setUnbindSubmitting(false);
          if (err) {
            showError?.(err);
            return;
          }
          setUnbindTarget(null);
          const binding = result?.binding || {};
          if (binding.notFound) {
            // Not an error — treat as no-op and just refresh so state
            // reflects the (already-consistent) truth.
            showSuccess?.(`${bindingKey} wasn't currently bound — refreshed.`);
          } else {
            showSuccess?.(
              restart
                ? `Unbound ${entry.displayName || bindingKey} — app restarting…`
                : `Unbound ${entry.displayName || bindingKey} (restart skipped).`
            );
          }
          setTimeout(refresh, 900);
        }
      );
    },
    [appName, refresh, showError, showSuccess]
  );

  const grouped = useMemo(() => {
    // Preserve server order; the group is here only if a future rev
    // wants to render section headers per-kind.
    return bindings;
  }, [bindings]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon
          sx={{
            color: isDarkMode ? "#94a3b8" : "#64748b",
            fontSize: "1.15rem",
          }}
        />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "#f1f5f9" : "#0f172a",
              lineHeight: 1.2,
            }}
          >
            Bound Services
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontSize: "0.75rem",
            }}
          >
            Daari services injected into this app's C2A_SYSTEM_ENV.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setBindOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            background: "#F97316",
            "&:hover": { background: "#EA580C" },
          }}
        >
          Bind Service
        </Button>
      </Box>

      {/* Body */}
      {loading && bindings.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : bindings.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${isDarkMode ? theme.palette.grey[800] : "#e2e8f0"}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            background: isDarkMode ? "rgba(255,255,255,0.02)" : "#f8fafc",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              mb: 1,
            }}
          >
            Not bound to any Daari service.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: isDarkMode ? "#64748b" : "#94a3b8",
              mb: 2,
            }}
          >
            Bind one to inject its credentials as
            {" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              C2A_SYSTEM_ENV
            </Box>
            {" "}
            at runtime.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setBindOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Bind Service
          </Button>
        </Box>
      ) : (
        <List
          disablePadding
          sx={{
            border: `1px solid ${isDarkMode ? theme.palette.grey[800] : "#e2e8f0"}`,
            borderRadius: 2,
            overflow: "hidden",
            background: isDarkMode ? theme.palette.grey[900] : "#fff",
          }}
        >
          {grouped.map((entry, idx) => {
            const meta = kindMeta(entry?.kind);
            const KindIcon = meta.Icon;
            const rowKey = entry?.k8sName || `${entry?.displayName}-${idx}`;
            const orphan = !entry?.kind || !entry?.k8sName;
            return (
              <ListItem
                key={rowKey}
                sx={{
                  borderTop:
                    idx === 0
                      ? "none"
                      : `1px solid ${isDarkMode ? theme.palette.grey[800] : "#f1f5f9"}`,
                  py: 1.25,
                  px: 2,
                  gap: 1,
                  alignItems: "center",
                }}
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {entry?.k8sName && !orphan && (
                      <Tooltip title="Open on Daari" arrow>
                        <IconButton
                          size="small"
                          onClick={() =>
                            onNavigateToDaari?.(kindToPath(entry.kind), entry.k8sName)
                          }
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        setMenuState({ anchor: e.currentTarget, entry })
                      }
                    >
                      <MoreHoriz fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <KindIcon sx={{ color: meta.tint }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? "#e2e8f0" : "#0f172a",
                        }}
                      >
                        {entry?.displayName || entry?.k8sName || "(unnamed)"}
                      </Typography>
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          border: `1px solid ${meta.tint}55`,
                          background: `${meta.tint}18`,
                          color: isDarkMode ? "#e2e8f0" : "#334155",
                        }}
                      />
                      {orphan && (
                        <Chip
                          size="small"
                          label="orphan"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            background: "rgba(239,68,68,0.15)",
                            color: "#b91c1c",
                            border: "1px solid rgba(239,68,68,0.35)",
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    entry?.k8sName ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontFamily: "monospace",
                          fontSize: "0.7rem",
                        }}
                      >
                        {entry.k8sName}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ color: "#b91c1c", fontSize: "0.7rem" }}
                      >
                        Source binding secret is missing — service may have
                        been deleted out-of-band.
                      </Typography>
                    )
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Menu
        anchorEl={menuState.anchor}
        open={Boolean(menuState.anchor)}
        onClose={closeMenu}
      >
        <MenuItem
          disabled={!menuState.entry?.k8sName}
          onClick={() => {
            const entry = menuState.entry;
            closeMenu();
            if (entry?.k8sName) {
              onNavigateToDaari?.(kindToPath(entry.kind), entry.k8sName);
            }
          }}
        >
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open on Daari</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const entry = menuState.entry;
            closeMenu();
            setUnbindTarget(entry);
          }}
        >
          <ListItemIcon>
            <UnbindIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>Unbind</ListItemText>
        </MenuItem>
      </Menu>

      <BindDialog
        open={bindOpen}
        onClose={() => setBindOpen(false)}
        appName={appName}
        existingBindings={bindings}
        onSuccess={handleBindSuccess}
        onError={showError}
      />

      <UnbindConfirmDialog
        open={Boolean(unbindTarget)}
        target={unbindTarget}
        submitting={unbindSubmitting}
        onCancel={() => setUnbindTarget(null)}
        onConfirm={(restart) => handleUnbind(unbindTarget, restart)}
      />
    </Box>
  );
}
